import { useEffect } from 'react';
import { assert } from '@chainflip/utils/assertion';
import useToast from '@/shared/hooks/useToast';
import { FLIP_SYMBOL } from '@/shared/utils/env';
import { getViemError } from '@/shared/utils/functions';
import type TokenAmount from '@/shared/utils/TokenAmount';
import useEvmScAccount from './useEvmScAccount';
import useEvmScCall from './useEvmScCall';

export default function useDelegateFlip({
  onSuccess,
  onSubmitted,
}: {
  onSuccess?: () => void;
  onSubmitted?: () => void;
} = {}) {
  const { delegationSDK: sdk } = useEvmScAccount();
  const evmToast = useToast('evm-delegate-flip');
  const scToast = useToast('sc-delegate-flip');

  useEffect(
    () => () => {
      evmToast.dismiss();
      scToast.dismiss();
    },
    [],
  );

  return useEvmScCall({
    fn: ({
      amount,
      operator,
      depositAmount,
    }: {
      depositAmount?: TokenAmount | null;
      operator: `cF${string}`;
      amount?: TokenAmount;
    }) => {
      assert(sdk, 'Delegation SDK not initialized');

      evmToast.loading('Submitting call to Ethereum...');

      if (depositAmount) return sdk.depositAndDelegate(operator, depositAmount, amount);
      return sdk.delegateFromBalance(operator, amount);
    },
    onSubmitted: ({ args: { amount } }) => {
      if (amount?.eq(0)) {
        evmToast.success('Successfully submitted call to switch operators');
      } else {
        evmToast.success(
          `Successfully submitted call to delegate ${amount ? amount.toFormatted() : 'all'} ${FLIP_SYMBOL} tokens`,
        );
      }
      scToast.loading('Waiting for confirmation of delegation on the State Chain...');
      onSubmitted?.();
    },
    onEvmError: (err) => evmToast.error('Delegation failed', { description: getViemError(err) }),
    onScError: (err) => {
      scToast.error('Delegation failed on the State Chain', { description: err.name });
    },
    onSuccess: () => {
      onSuccess?.();
      scToast.success('Delegation confirmed on the State Chain!');
    },
  });
}
